import { LEVELS } from 'app/levels/levels';
import { LevelContextProvider } from 'app/src/game/LevelContext';
import LocalStorageManager from 'app/src/utils/LocalStorageManager';
import * as React from 'react';

const FLAT_LEVELS = LEVELS.map((section) => section.levels).reduce((a, b) => [...a, ...b]);

type Props = {
	children: React.ReactNode;
};

export default function LevelController({ children }: Props): React.ReactElement {
	const localHighestCompletedLevel = LocalStorageManager.sharedInstance.getHighestCompletedLevel();

	const [currentLevel, setCurrentLevel] = React.useState<string | null>(null);
	const [highestCompletedLevel, setHighestCompletedLevel] = React.useState<string | null>(
		localHighestCompletedLevel,
	);

	const getNextLevel = (): string | null => {
		const levelIndex = currentLevel === null ? -1 : FLAT_LEVELS.indexOf(currentLevel);

		if (levelIndex < FLAT_LEVELS.length - 1) {
			return FLAT_LEVELS[levelIndex + 1];
		}

		return null;
	};

	const canPlayLevel = (level: string): boolean => {
		const levelIndex = FLAT_LEVELS.indexOf(level);

		if (levelIndex === null) {
			return false;
		}

		const highestCompletedIndex =
			highestCompletedLevel === null ? -1 : FLAT_LEVELS.indexOf(highestCompletedLevel);

		return levelIndex <= highestCompletedIndex + 1;
	};

	const unlockAllLevels = (): void => {
		setHighestCompletedLevel(FLAT_LEVELS[FLAT_LEVELS.length - 1]);
	};

	const resetUnlockedLevels = (): void => {
		setHighestCompletedLevel(null);
	};

	const completeLevel = (level: string): void => {
		const completedIndex = FLAT_LEVELS.indexOf(level);

		if (completedIndex === null) {
			throw new Error('Invalid level.');
		}

		const highestCompletedIndex =
			highestCompletedLevel === null ? -1 : FLAT_LEVELS.indexOf(highestCompletedLevel);

		if (completedIndex > highestCompletedIndex) {
			setHighestCompletedLevel(level);
			LocalStorageManager.sharedInstance.setHighestCompletedLevel(level);
		}
	};

	const setCurrentLevelIfValid = (level: string | null): void => {
		if (level === null) {
			setCurrentLevel(null);
		} else {
			const levelIndex = FLAT_LEVELS.indexOf(level);
			if (typeof levelIndex === 'number') {
				setCurrentLevel(level);
			}
		}
	};

	return (
		<LevelContextProvider
			value={{
				allLevels: LEVELS,
				currentLevel,
				setCurrentLevel: setCurrentLevelIfValid,
				completeLevel,
				getNextLevel,
				canPlayLevel,
				unlockAllLevels,
				resetUnlockedLevels,
			}}
		>
			{children}
		</LevelContextProvider>
	);
}
